import { useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { CssBaseline } from '@mui/material'
import { SnackbarProvider } from 'notistack'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { GroupNavigationProvider } from './contexts/GroupNavigationContext'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import UpdateNotification from './components/UpdateNotification'
import { NotificationManager } from './components/NotificationManager'
import SplashScreen from './components/SplashScreen'

// Pages
import Dashboard from './pages/Dashboard'
import Login from './pages/Login'
import Register from './pages/Register'
import GroupList from './pages/GroupList'
import GroupDetail from './pages/GroupDetail'
import CreatePrayerRequest from './pages/CreatePrayerRequest'
import PrayerRequestDetail from './pages/PrayerRequestDetail'
import CreateDiscussion from './pages/CreateDiscussion'
import DiscussionDetail from './pages/DiscussionDetail'
import FamilyList from './pages/FamilyList'
import FamilyDetail from './pages/FamilyDetail'
import Profile from './pages/Profile'
import UserProfile from './pages/UserProfile'
import AcceptInvite from './pages/AcceptInvite'
import AdminPortal from './pages/AdminPortal'
import ResetPassword from './pages/ResetPassword'

function AppRoutes() {
  const navigate = useNavigate()
  const { loading } = useAuth()

  useEffect(() => {
    const clearBadge = async () => {
      navigator.clearAppBadge?.()
      // Tell the service worker to clear its badge state too
      const reg = await navigator.serviceWorker?.ready
      reg?.active?.postMessage({ type: 'RESET_BADGE' })
    }
    window.addEventListener('focus', clearBadge)
    clearBadge()
    return () => window.removeEventListener('focus', clearBadge)
  }, [])

  // Listen for navigation messages from the service worker (notification taps)
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'NAVIGATE_TO' && event.data?.url) {
        navigate(event.data.url)
      }
    }
    navigator.serviceWorker?.addEventListener('message', handler)
    return () => navigator.serviceWorker?.removeEventListener('message', handler)
  }, [navigate])

  if (loading) {
    return <SplashScreen />
  }

  return (
    <>
      <NotificationManager />
      <GroupNavigationProvider>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="invite/:code" element={<AcceptInvite />} />
          <Route path="reset-password" element={<ResetPassword />} />

          {/* Protected routes */}
          <Route path="groups" element={
            <ProtectedRoute><GroupList /></ProtectedRoute>
          } />
          <Route path="groups/:id" element={
            <ProtectedRoute><GroupDetail /></ProtectedRoute>
          } />
          <Route path="groups/:groupId/new-prayer" element={
            <ProtectedRoute><CreatePrayerRequest /></ProtectedRoute>
          } />
          <Route path="prayer-requests/:id" element={
            <ProtectedRoute><PrayerRequestDetail /></ProtectedRoute>
          } />
          <Route path="groups/:groupId/new-discussion" element={
            <ProtectedRoute><CreateDiscussion /></ProtectedRoute>
          } />
          <Route path="discussions/:id" element={
            <ProtectedRoute><DiscussionDetail /></ProtectedRoute>
          } />
          <Route path="families" element={
            <ProtectedRoute><FamilyList /></ProtectedRoute>
          } />
          <Route path="families/:id" element={
            <ProtectedRoute><FamilyDetail /></ProtectedRoute>
          } />
          <Route path="profile" element={
            <ProtectedRoute><Profile /></ProtectedRoute>
          } />
          <Route path="users/:userId" element={
            <ProtectedRoute><UserProfile /></ProtectedRoute>
          } />
          <Route path="admin" element={
            <ProtectedRoute><AdminPortal /></ProtectedRoute>
          } />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      </GroupNavigationProvider>
    </>
  )
}

function App() {
  return (
    <ThemeProvider>
      <CssBaseline />
      <UpdateNotification />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  )
}

export default App
